import { Context } from '../context/Context';
import { Camera } from '../camera';
import { FullscreenQuad } from './FullscreenQuad';
import { GBuffer } from './GBuffer';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';
import { vec3 } from 'gl-matrix';

export class SSAOPass {
  private static readonly KERNEL_SAMPLES = 16;

  // rawFBO holds the SSAO compute output; ssaoFBO holds the final 3×3
  // cross-bilateral blur output and is the public handle consumed by
  // deferred-batched / forward-transparent / liquid-surface.
  private rawFBO!: WebGLFramebuffer;
  private ssaoFBO!: WebGLFramebuffer;
  private rawTexture!: RenderTexture;
  ssaoTexture!: RenderTexture;
  private noiseTexture!: RenderTexture;
  whiteTexture!: RenderTexture;
  private kernel!: Float32Array;
  private kernelVecs!: Float32Array[];
  private width!: number;
  private height!: number;

  private resolutionDivisor = 2;

  init(ctx: Context, resolutionDivisor: number = 2): void {
    const gl = ctx.gl;
    this.resolutionDivisor = resolutionDivisor;
    this.width = Math.max(1, Math.floor(ctx.viewPort.width / this.resolutionDivisor));
    this.height = Math.max(1, Math.floor(ctx.viewPort.height / this.resolutionDivisor));

    // Generate hemisphere kernel and pre-slice into per-sample vecs.
    this.kernel = this.generateKernel();
    this.kernelVecs = [];
    for (let i = 0; i < SSAOPass.KERNEL_SAMPLES; i++) {
      this.kernelVecs.push(this.kernel.slice(i * 3, i * 3 + 3));
    }

    // Create noise texture (4x4 random rotation vectors)
    this.noiseTexture = new RenderTexture();
    this.noiseTexture.glTexture = this.createNoiseTexture(gl);

    // Raw SSAO compute target (R8, half-res). The temporal pass reads from it.
    this.rawTexture = new RenderTexture();
    this.rawTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.R8,
      gl.RED,
      gl.UNSIGNED_BYTE,
      gl.LINEAR,
    );
    this.rawFBO = createFramebuffer(gl, this.rawTexture.glTexture!);

    // Final blurred output, exposed publicly via `ssaoTexture` (R8, half-res).
    this.ssaoTexture = new RenderTexture();
    this.ssaoTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.R8,
      gl.RED,
      gl.UNSIGNED_BYTE,
      gl.LINEAR,
    );
    this.ssaoFBO = createFramebuffer(gl, this.ssaoTexture.glTexture!);

    // White 1x1 fallback texture (used when SSAO is disabled)
    this.whiteTexture = new RenderTexture();
    this.whiteTexture.glTexture = this.createWhiteTexture(gl);
  }

  setResolutionDivisor(ctx: Context, divisor: number): void {
    if (divisor === this.resolutionDivisor) return;
    this.resolutionDivisor = divisor;
    const gl = ctx.gl;
    if (this.rawFBO) gl.deleteFramebuffer(this.rawFBO);
    if (this.ssaoFBO) gl.deleteFramebuffer(this.ssaoFBO);
    if (this.rawTexture?.glTexture) gl.deleteTexture(this.rawTexture.glTexture);
    if (this.ssaoTexture?.glTexture) gl.deleteTexture(this.ssaoTexture.glTexture);

    this.width = Math.max(1, Math.floor(ctx.viewPort.width / this.resolutionDivisor));
    this.height = Math.max(1, Math.floor(ctx.viewPort.height / this.resolutionDivisor));

    this.rawTexture = new RenderTexture();
    this.rawTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.R8,
      gl.RED,
      gl.UNSIGNED_BYTE,
      gl.LINEAR,
    );
    this.rawFBO = createFramebuffer(gl, this.rawTexture.glTexture!);

    this.ssaoTexture = new RenderTexture();
    this.ssaoTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.R8,
      gl.RED,
      gl.UNSIGNED_BYTE,
      gl.LINEAR,
    );
    this.ssaoFBO = createFramebuffer(gl, this.ssaoTexture.glTexture!);
  }

  renderSSAO(ctx: Context, gBuffer: GBuffer, quad: FullscreenQuad, camera: Camera): void {
    const gl = ctx.gl;

    // === SSAO computation pass → rawFBO ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.rawFBO);
    gl.viewport(0, 0, this.width, this.height);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const ssaoShader = ctx.engine.shaderManager.getSsaoShader();
    ctx.setShader(ssaoShader);

    ssaoShader.uniformsContainer
      .setDepthMap(gBuffer.depthTexture)
      .setNormalMap(gBuffer.normalTexture)
      .setNoiseMap(this.noiseTexture)
      .setViewMatrix(camera.modelViewMatrix)
      .setProjectionMatrix(camera.projectionMatrix)
      .setInvProjection(camera.invProjection)
      .setRadius(0.3)
      .setBias(0.01)
      .setNoiseScale([this.width / 4, this.height / 4])
      .setTaaFrameIndex(camera.taaFrameIndex);

    for (let i = 0; i < SSAOPass.KERNEL_SAMPLES; i++) {
      ssaoShader.uniformsContainer.setKernel(i, this.kernelVecs[i]);
    }

    ssaoShader.validate();
    quad.draw(gl);

    // === Single-pass 3×3 cross bilateral blur → ssaoFBO ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.ssaoFBO);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const blurShader = ctx.engine.shaderManager.getSsaoBlurCrossShader();
    ctx.setShader(blurShader);

    blurShader.uniformsContainer
      .setSsaoMap(this.rawTexture)
      .setDepthMap(gBuffer.depthTexture)
      .setInvProjection(camera.invProjection)
      .setTexelSize([1.0 / this.width, 1.0 / this.height]);

    blurShader.validate();
    quad.draw(gl);
  }

  private generateKernel(): Float32Array {
    const n = SSAOPass.KERNEL_SAMPLES;
    const kernel = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      let sample = vec3.fromValues(
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
        Math.random(), // hemisphere: only positive Z
      );
      vec3.normalize(sample, sample);

      // Scale so more samples are closer to the surface
      let scale = i / n;
      scale = 0.1 + scale * scale * 0.9;
      vec3.scale(sample, sample, scale);

      kernel[i * 3] = sample[0];
      kernel[i * 3 + 1] = sample[1];
      kernel[i * 3 + 2] = sample[2];
    }
    return kernel;
  }

  private createNoiseTexture(gl: WebGL2RenderingContext): WebGLTexture {
    // 4x4 texture with random tangent-space rotation vectors
    const size = 4;
    const data = new Float32Array(size * size * 3);
    for (let i = 0; i < size * size; i++) {
      data[i * 3] = Math.random() * 2 - 1;
      data[i * 3 + 1] = Math.random() * 2 - 1;
      data[i * 3 + 2] = 0; // rotate around Z (the normal in tangent space)
    }

    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB16F, size, size, 0, gl.RGB, gl.FLOAT, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
    return tex;
  }

  private createWhiteTexture(gl: WebGL2RenderingContext): WebGLTexture {
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.R8,
      1,
      1,
      0,
      gl.RED,
      gl.UNSIGNED_BYTE,
      new Uint8Array([255]),
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    return tex;
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteFramebuffer(this.rawFBO);
    gl.deleteFramebuffer(this.ssaoFBO);
    if (this.rawTexture.glTexture) gl.deleteTexture(this.rawTexture.glTexture);
    if (this.ssaoTexture.glTexture) gl.deleteTexture(this.ssaoTexture.glTexture);
    if (this.noiseTexture.glTexture) gl.deleteTexture(this.noiseTexture.glTexture);
  }
}
